import React, { useState, useRef } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, Button } from "../components/UI";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState({ pct: 0, message: "" });
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/import/job/${jobId}`);
        const j = r.data;
        setProgress({ pct: j.progress || 0, message: j.message || "Working…" });
        if (j.status === "done") {
          stopPoll();
          setUploading(false);
          setResult(j.result);
          toast.success(`${j.result.created_orders} orders imported`);
        } else if (j.status === "failed") {
          stopPoll();
          setUploading(false);
          setResult({ error: j.error || "Import failed" });
          toast.error(j.error || "Import failed");
        }
      } catch (err) {
        stopPoll();
        setUploading(false);
        toast.error(formatApiError(err));
        setResult({ error: formatApiError(err) });
      }
    }, 2000);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setUploading(true);
    setResult(null);
    setProgress({ pct: 0, message: "Uploading file…" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/orders-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000, // upload alone should not exceed 60s
      });
      // Background job pattern: poll for progress
      const jobId = r.data.job_id;
      if (!jobId) {
        // Backward compat: direct result
        setResult(r.data);
        setUploading(false);
        toast.success(`${r.data.created_orders} orders imported`);
        return;
      }
      setProgress({ pct: 5, message: r.data.message || "Import queued…" });
      pollJob(jobId);
    } catch (err) {
      setUploading(false);
      toast.error(formatApiError(err));
      setResult({ error: formatApiError(err) });
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "singh_offset_import_template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl" data-testid="import-page">
      <PageHeader
        title="Import Historical Data"
        subtitle="Upload your past sales from Busy Accounting (or any Excel/XLSX) to instantly build customer order history"
      />

      <div className="card-flat p-3 mb-5 bg-blue-50 border-l-4 border-blue-500 text-sm text-blue-900" data-testid="receipt-hint">
        💡 <b>Receipt Excel (Payment register)</b> upload karne ke liye jaaye → <a href="/payment-dashboard" className="text-[#C1121F] font-bold underline">Payment Dashboard</a> → top right "Import Busy Excel" button. Wahaan se Busy ka Receipt Voucher Excel upload karein — har payment automatically invoice ke saath link ho jayegi.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-6" data-testid="upload-card">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={20} className="text-[#C1121F]"/>
            <h3 className="font-display font-bold text-xl">Upload Sales Excel</h3>
          </div>
          <p className="text-sm text-[#475569] mb-5">
            Export your sales register from Busy Accounting as Excel (.xlsx). Make sure columns include:
            <b> Date, Customer, Item, Qty, Rate</b> (Bill No. and Amount are optional).
          </p>          <input
            type="file"
            accept=".xlsx,.xls"
            data-testid="file-input"
            onChange={(e) => setFile(e.target.files[0])}
            className="block w-full text-sm text-[#475569] file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:font-semibold file:bg-[#FEF2F2] file:text-[#C1121F] hover:file:bg-[#FECACA]"
            disabled={uploading}
          />
          {file && (
            <div className="mt-3 text-xs text-[#475569] flex items-center gap-2">
              <FileSpreadsheet size={14} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
          <Button onClick={upload} disabled={!file || uploading} className="mt-4 w-full justify-center" data-testid="upload-btn">
            {uploading ? <><Loader2 size={15} className="animate-spin"/> Importing…</> : <><Upload size={15}/> Import Now</>}
          </Button>

          {uploading && (
            <div className="mt-4" data-testid="progress">
              <div className="flex justify-between text-xs text-[#475569] mb-1">
                <span>{progress.message}</span>
                <span className="font-mono-num font-bold">{progress.pct}%</span>
              </div>
              <div className="h-2 bg-[#E5E7EB] rounded overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#C1121F] to-amber-500 transition-all" style={{ width: `${progress.pct}%` }}/>
              </div>
              <div className="text-[10px] text-[#94A3B8] mt-2">
                💡 Large files run in background — you can keep this tab open. Don&apos;t worry if it takes a few minutes.
              </div>
            </div>
          )}
        </div>

        <div className="card-flat p-6" data-testid="template-card">
          <div className="flex items-center gap-2 mb-3">
            <Download size={20} className="text-[#475569]"/>
            <h3 className="font-display font-bold text-xl">Need a Template?</h3>
          </div>
          <p className="text-sm text-[#475569] mb-5">
            Download a ready-made Excel template with the exact column structure. Fill it in with your old data and upload — system creates customers and orders automatically.
          </p>
          <Button variant="secondary" onClick={downloadTemplate} className="w-full justify-center" data-testid="dl-template-btn">
            <Download size={15}/> Download Template
          </Button>
          <div className="mt-5 p-3 bg-[#F8F9FA] rounded text-xs text-[#475569] space-y-1.5">
            <div className="font-semibold text-[#0F172A]">Tips for best results:</div>
            <div>• Group multiple line items by Bill No (same bill = one order)</div>
            <div>• Customer names should be consistent (Apollo vs Apollo Hospital)</div>
            <div>• Past dates are preserved — Reorder Radar uses them for predictions</div>
            <div>• All imported orders are marked Delivered</div>
          </div>
        </div>
      </div>

      {result && (
        <div className="mt-6 card-flat p-6" data-testid="import-result">
          {result.error ? (
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20}/>
              <div>
                <div className="font-display font-bold text-red-600">Import failed</div>
                <div className="text-sm text-[#475569] mt-1">{result.error}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={22}/>
              <div className="flex-1">
                <div className="font-display font-bold text-green-700 text-lg">Import successful!</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Orders Created</div>
                    <div className="font-display font-bold text-2xl text-[#C1121F]">{result.created_orders}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">New Customers</div>
                    <div className="font-display font-bold text-2xl text-[#0F172A]">{result.created_customers}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Total Bills Found</div>
                    <div className="font-display font-bold text-2xl text-[#0F172A]">{result.groups_found}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Skipped Rows</div>
                    <div className="font-display font-bold text-2xl text-[#94A3B8]">{result.skipped_rows}</div>
                  </div>
                </div>
                <div className="text-xs text-[#475569] mt-4">
                  Head over to <a href="/reorders" className="text-[#C1121F] font-semibold hover:underline">Reorder Radar</a> — your AI predictions are now powered by this real history!
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
